import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WorkspaceAccessDropdown.css';

function WorkspaceAccessDropdown({ 
    member, 
    isOpen, 
    onClose, 
    onUpdate, 
    user, 
    org,
    isCurrentUserAdmin 
}) {
    const [workspaces, setWorkspaces] = useState([]);
    const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
    const [editingWorkspaces, setEditingWorkspaces] = useState({});
    const [fullAccessMode, setFullAccessMode] = useState({});
    const [memberWorkspaceIds, setMemberWorkspaceIds] = useState([]);
    const [loadingMemberAccess, setLoadingMemberAccess] = useState(false);

    const memberKey = member.id || member.email;
    const currentEditingWorkspaces = editingWorkspaces[memberKey] || [];
    const isFullAccessMode = fullAccessMode[memberKey] || false;
    const selectedWorkspaceCount = currentEditingWorkspaces.length;

    // Fetch workspaces and member workspace access when dropdown opens
    useEffect(() => {
        if (isOpen) {
            fetchWorkspaces();
            if (member.accessLevel === 'limited') {
                fetchMemberWorkspaceAccess();
            }
        }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.workspace-access-dropdown')) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen, onClose]);

    // Initialize state when dropdown opens and data is loaded
    useEffect(() => {
        if (isOpen && workspaces.length > 0) {
            // For limited access users, wait for both workspaces and member access data
            if (member.accessLevel === 'limited') {
                if (!loadingMemberAccess && memberWorkspaceIds.length >= 0) {
                    initializeState();
                }
            } else {
                // For full access users, only need workspaces
                initializeState();
            }
        }
    }, [isOpen, workspaces, memberWorkspaceIds, loadingMemberAccess]);

    const fetchWorkspaces = async () => {
        setLoadingWorkspaces(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/get-workspaces/${user.id}/${org.id}`, { withCredentials: true });
            setWorkspaces(response.data);
        } catch (error) {
            console.error('Error fetching workspaces:', error);
        } finally {
            setLoadingWorkspaces(false);
        }
    };

    const fetchMemberWorkspaceAccess = async () => {
        setLoadingMemberAccess(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/orgs/get-member-workspace-access/${org.id}/${member.id}`, 
                { withCredentials: true }
            );
            setMemberWorkspaceIds(response.data.workspaceIds || []);
        } catch (error) {
            console.error('Error fetching member workspace access:', error);
            setMemberWorkspaceIds([]);
        } finally {
            setLoadingMemberAccess(false);
        }
    };

    const initializeState = () => {
        // Use the fetched member workspace IDs for limited access users, otherwise use the member's stored workspaceIds
        const memberWorkspaces = member.accessLevel === 'limited' ? memberWorkspaceIds : (member.workspaceIds || []);
        
        // Initialize workspace selections based on current member state
        let initialWorkspaces;
        if (member.orgRole === 'admin' || member.accessLevel === 'full') {
            // For full access, select all available workspaces
            initialWorkspaces = workspaces.map(w => w.id);
        } else {
            // For limited access, use the fetched workspace access data
            initialWorkspaces = memberWorkspaces;
        }
        
        setEditingWorkspaces(prev => ({
            ...prev,
            [memberKey]: [...initialWorkspaces]
        }));
        
        // Initialize full access mode based on the member's actual access level, not workspace selection
        const isFullAccess = member.orgRole === 'admin' || member.accessLevel === 'full';
        
        setFullAccessMode(prev => ({
            ...prev,
            [memberKey]: isFullAccess
        }));
    };

    const handleFullAccessToggle = () => {
        const newFullAccessMode = !isFullAccessMode;
        setFullAccessMode(prev => ({
            ...prev,
            [memberKey]: newFullAccessMode
        }));
        
        if (newFullAccessMode) {
            // Switching to full access - select all workspaces
            setEditingWorkspaces(prev => ({
                ...prev,
                [memberKey]: workspaces.map(w => w.id)
            }));
        } else {
            // Switching to limited access - use the fetched workspace access data
            const currentWorkspaces = member.accessLevel === 'limited' ? memberWorkspaceIds : (member.workspaceIds || []);
            setEditingWorkspaces(prev => ({
                ...prev,
                [memberKey]: currentWorkspaces
            }));
        }
    };

    const handleWorkspaceToggleEdit = (workspaceId) => {
        const wasSelected = currentEditingWorkspaces.includes(workspaceId);
        const updatedWorkspaces = wasSelected
            ? currentEditingWorkspaces.filter(id => id !== workspaceId)
            : [...currentEditingWorkspaces, workspaceId];
        
        setEditingWorkspaces(prev => ({
            ...prev,
            [memberKey]: updatedWorkspaces
        }));
        
        // Only turn off the toggle if a workspace was deselected
        if (wasSelected) {
            setFullAccessMode(prev => ({
                ...prev,
                [memberKey]: false
            }));
        }
        // Don't automatically turn on the toggle when all workspaces are selected
    };

    const handleCancel = () => {
        // Reset to original state
        const memberWorkspaces = member.accessLevel === 'limited' ? memberWorkspaceIds : (member.workspaceIds || []);
        let initialWorkspaces;
        if (member.orgRole === 'admin' || member.accessLevel === 'full') {
            // For full access, select all available workspaces
            initialWorkspaces = workspaces.map(w => w.id);
        } else {
            // For limited access, use the fetched workspace access data
            initialWorkspaces = memberWorkspaces;
        }
        
        setEditingWorkspaces(prev => ({
            ...prev,
            [memberKey]: [...initialWorkspaces]
        }));
        
        // Reset full access mode based on the member's actual access level, not workspace selection
        const isFullAccess = member.orgRole === 'admin' || member.accessLevel === 'full';
        
        setFullAccessMode(prev => ({
            ...prev,
            [memberKey]: isFullAccess
        }));
        onClose();
    };

    const handleUpdate = async () => {
        // Check if current user is admin - only admins can change workspace access
        if (!isCurrentUserAdmin) {
            onUpdate('Only admins can change workspace access.');
            onClose();
            return;
        }
        
        try {
            // Determine access level based on the toggle
            const newAccessLevel = isFullAccessMode ? 'full' : 'limited';
            const newWorkspaceIds = currentEditingWorkspaces;
            
            await onUpdate(null, newAccessLevel, newWorkspaceIds);
            onClose();
        } catch (error) {
            const errorType = member.isInvite ? 'invite' : 'member';
            onUpdate(`Failed to update ${errorType} workspace access. Please try again.`);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="workspace-access-dropdown">
            <div className="selection-menu workspace-access-menu">
                <div className="workspace-access-list">
                    {(loadingWorkspaces || (member.accessLevel === 'limited' && loadingMemberAccess)) ? (
                        <div className="workspace-access-item">
                            <span className="workspace-access-text">Loading workspaces...</span>
                        </div>
                    ) : workspaces.length > 0 ? (
                        <div className="workspace-dropdown-content">
                            <div className="workspace-dropdown-header">
                                <div className="workspace-dropdown-title-group">
                                    <span className="workspace-dropdown-kicker">Workspace access</span>
                                </div>
                                <div className="workspace-dropdown-selection-count">
                                    {isFullAccessMode ? 'All workspaces' : `${selectedWorkspaceCount} selected`}
                                </div>
                            </div>

                            <div className="workspace-dropdown-toggle-card">
                                <div className="workspace-dropdown-toggle-copy">
                                    <span className="workspace-dropdown-toggle-title">All current and future workspaces</span>
                                    <span className="workspace-dropdown-toggle-description">
                                        Turn this on for full workspace access.
                                    </span>
                                </div>
                                <div className="workspace-dropdown-toggle-switch">
                                    <input
                                        type="checkbox" 
                                        id={`share-all-workspaces-${member.id || member.email}`} 
                                        className="workspace-dropdown-toggle-input"
                                        checked={Boolean(isFullAccessMode)}
                                        onChange={handleFullAccessToggle}
                                    />
                                    <label
                                        htmlFor={`share-all-workspaces-${member.id || member.email}`}
                                        className="workspace-dropdown-toggle-label"
                                    ></label>
                                </div>
                            </div>

                            <div className="workspace-dropdown-list-card">
                                <div className="workspace-dropdown-list-header">
                                    <span className="workspace-dropdown-list-title">Specific workspaces</span>
                                    <span className="workspace-dropdown-list-subtitle">
                                        {isFullAccessMode
                                            ? 'Selection is locked while full access is enabled.'
                                            : 'Select one or more workspaces below.'}
                                    </span>
                                </div>
                                <div className="workspace-dropdown-list">
                                {[...workspaces]
                                    .sort((a, b) => {
                                        const aHasAccess = currentEditingWorkspaces.includes(a.id);
                                        const bHasAccess = currentEditingWorkspaces.includes(b.id);
                                        if (aHasAccess !== bHasAccess) return aHasAccess ? -1 : 1;
                                        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
                                    })
                                    .map(workspace => (
                                        <div key={workspace.id} className="workspace-dropdown-item">
                                            <input
                                            type="checkbox" 
                                            id={`workspace-${workspace.id}-${member.id || member.email}`} 
                                            className="workspace-dropdown-checkbox"
                                            checked={Boolean(currentEditingWorkspaces.includes(workspace.id))}
                                            onChange={() => handleWorkspaceToggleEdit(workspace.id)}
                                            disabled={Boolean(isFullAccessMode)}
                                        />
                                            <label
                                                htmlFor={`workspace-${workspace.id}-${member.id || member.email}`}
                                                className="workspace-dropdown-checkbox-label"
                                            >
                                            {workspace.name}
                                        </label>
                                    </div>
                                ))}
                                </div>
                            </div>

                            <div className="workspace-access-actions">
                                <button className="cancel-button" onClick={handleCancel}>
                                    Cancel
                                </button>
                                <button className="update-button" onClick={handleUpdate}>
                                    Update
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="workspace-access-item">
                            <span className="workspace-access-text">No workspaces available</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default WorkspaceAccessDropdown; 